import { createSlice } from '@reduxjs/toolkit';
import { apiSlice } from '../app/apiSlice';

// RTK Query API endpoints
export const teacherClassesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Teacher Live Classes endpoints
    createTeacherLiveClass: builder.mutation({
      query: (data) => ({
        url: '/api/v1/en/employee/teacher/live-class/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TeacherLiveClass'],
    }),
    
    getTeacherLiveClass: builder.query({
      query: (id) => `/api/v1/en/employee/teacher/live-class/view/${id}`,
      transformResponse: (response) => {
        // Return the live class data
        return response?.data?.data || {};
      },
      providesTags: (result, error, id) => [{ type: 'TeacherLiveClass', id }],
    }),
    
    getTeacherLiveClasses: builder.query({
      query: (params = {}) => {
        const queryString = new URLSearchParams();
        if (params.courseId) queryString.append('courseId', params.courseId);
        if (params.courseModuleId) queryString.append('courseModuleId', params.courseModuleId);
        if (params.limit) queryString.append('limit', params.limit);
        if (params.offset) queryString.append('offset', params.offset);
        
        return `/api/v1/en/employee/teacher/live-class/list?${queryString.toString()}`;
      },
      transformResponse: (response) => {
        // Return the full response structure with classes array
        return response?.data?.data || { classes: [], countDocuments: 0 };
      },
      providesTags: ['TeacherLiveClass'],
    }),
    
    updateTeacherLiveClass: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/employee/teacher/live-class/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLiveClass', id }],
    }),
    
    deleteTeacherLiveClass: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/employee/teacher/live-class/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeacherLiveClass'],
    }),
    
    // Teacher Lectures endpoints
    createTeacherLecture: builder.mutation({
      query: (data) => ({
        url: '/api/v1/en/employee/teacher/lectures/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TeacherLecture'],
    }),
    
    getTeacherLecture: builder.query({
      query: (id) => `/api/v1/en/employee/teacher/lectures/view/${id}`,
      transformResponse: (response) => {
        // Return the lecture data
        return response?.data?.data || {};
      },
      providesTags: (result, error, id) => [{ type: 'TeacherLecture', id }],
    }),
    
    uploadTeacherLecture: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/employee/teacher/lectures/upload/${id}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLecture', id }],
    }),
    
    // Update video content for existing lecture
    updateTeacherLectureVideo: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/employee/teacher/lectures/update-video/${id}`,
        method: 'PATCH',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLecture', id }],
    }),
    
    updateTeacherLecture: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/employee/teacher/lectures/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLecture', id }],
    }),
    
    getTeacherLectures: builder.query({
      query: (params = {}) => {
        const queryString = new URLSearchParams();
        if (params.courseId) queryString.append('courseId', params.courseId);
        if (params.courseModuleId) queryString.append('courseModuleId', params.courseModuleId);
        
        return `/api/v1/en/employee/teacher/lectures/list?${queryString.toString()}`;
      },
      transformResponse: (response) => {
        // Return the full response structure with lectures array
        return response?.data?.data || { lectures: [], total: 0 };
      },
      providesTags: ['TeacherLecture'],
    }),
    
    deleteTeacherLecture: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/employee/teacher/lectures/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeacherLecture'],
    }),
    
    getTeacherCoursesForClasses: builder.query({
      query: () => '/api/v1/en/employee/teacher/course/list-all',
      transformResponse: (response) => {
        console.log('Teacher Classes - Courses API Response:', response);
        // Based on the API response structure: data.data.courses
        return response?.data?.data?.courses || [];
      },
      providesTags: ['TeacherCourse'],
    }),
    
    getTeacherCourse: builder.query({
      query: (id) => `/api/v1/en/employee/teacher/course/view/${id}`,
      transformResponse: (response) => {
        console.log('Teacher Course Details API Response:', response);
        const courseData = response?.data?.data?.course || response?.data?.data || {};
        
        // Ensure modules are accessible - the API returns modules in course.modules
        if (courseData.modules) {
          console.log('Course modules found:', courseData.modules);
        }
        
        return courseData;
      },
      providesTags: (result, error, id) => [{ type: 'TeacherCourse', id }],
    }),

    // List timezones for teacher
    listTeacherTimezones: builder.query({
      query: ({ limit = 500 } = {}) => 
        `/api/v1/en/timezone/list-all?limit=${limit}`,
      transformResponse: (response) => {
        // The correct path is response.data.data.timeZone.timeZone
        const timezones = response?.data?.data?.timeZone?.timeZone || [];
        return Array.isArray(timezones) ? timezones : [];
      },
      providesTags: ["TeacherTimezones"],
    }),

    // Get all teachers for teacher assignments
    getAllTeachers: builder.query({
      query: ({ limit = 1000 } = {}) => 
        `/api/v1/en/admin/employee/teachers/get-all?isTeacher=3`,
      transformResponse: (response) => {
        console.log('Teachers API Response:', response);
        return response?.data?.data || { data: [], totalCount: 0 };
      },
      providesTags: ["Teachers"],
    }),


    getClassRange: builder.query({
      query: (id) => `/api/v1/en/employee/teacher/live-class/range/view/${id}`,
      transformResponse: (response) => {
        // Extract classIds from liveClassRange
        const liveClassRange = response?.data?.data?.liveClassRange || {};
        return {
          ...liveClassRange,
          classes: liveClassRange.classIds || []
        };
      },
      providesTags: (result, error, id) => [{ type: "ClassRange", id }],
    }),


   // Delete Class Range
    deleteClassRange: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/employee/teacher/live-class/range/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ClassRange"],
    }),

   getClassRanges: builder.query({
      query: (params = {}) => {
        const queryParts = [];
        if (params.courseId) queryParts.push(`courseId=${params.courseId}`);
        if (params.courseModuleId) {
          // Handle both single moduleId and array of moduleIds (for "all" case)
          if (Array.isArray(params.courseModuleId)) {
            queryParts.push(
              `courseModuleId=${params.courseModuleId.join(",")}`
            );
          } else {
            queryParts.push(`courseModuleId=${params.courseModuleId}`);
          }
        }
        if (params.limit) queryParts.push(`limit=${params.limit}`);
        if (params.offset) queryParts.push(`offset=${params.offset}`);

        // Add filter parameters for class ranges
        if (params.startTime) queryParts.push(`startTime=${params.startTime}`);
        if (params.endTime) queryParts.push(`endTime=${params.endTime}`);
        if (params.status && params.status !== "all")
          queryParts.push(`status=${params.status}`);

        const queryString = queryParts.join("&");
        return `/api/v1/en/employee/teacher/live-class/list?${queryString}`;
      },
      transformResponse: (response) => {
        // Return the classes array as ranges - based on the API response structure
        return response?.data?.data || { classes: [], countDocuments: 0 };
      },
      providesTags: ["ClassRange"],
    }),
    
    // Update class range (for editing class ranges)
    updateTeacherClassRange: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/employee/teacher/live-class/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLiveClass', id }],
    }),

    // Individual class APIs (for classes within a range)
    getLiveClass: builder.query({
      query: (id) => `/api/v1/en/employee/teacher/live-class/view/${id}`,
      transformResponse: (response) => {
        // Extract the liveClass from the nested response structure
        return response?.data?.data?.liveClass || response?.data?.data || {};
      },
      providesTags: (result, error, id) => [{ type: 'TeacherLiveClass', id }],
    }),

    updateIndividualClass: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/employee/teacher/live-class/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLiveClass', id }],
    }),

    // Alias endpoints for compatibility with existing code
    // These use teacher-specific endpoints
    getLiveClasses: builder.query({
      query: (params = {}) => {
        const queryParts = [];
        if (params.courseId) queryParts.push(`courseId=${params.courseId}`);
        if (params.courseModuleId) {
          if (Array.isArray(params.courseModuleId)) {
            queryParts.push(`courseModuleId=${params.courseModuleId.join(",")}`);
          } else {
            queryParts.push(`courseModuleId=${params.courseModuleId}`);
          }
        }
        if (params.limit) queryParts.push(`limit=${params.limit}`);
        if (params.offset) queryParts.push(`offset=${params.offset}`);
        if (params.startTime) queryParts.push(`startTime=${params.startTime}`);
        if (params.endTime) queryParts.push(`endTime=${params.endTime}`);
        if (params.status && params.status !== "all") queryParts.push(`status=${params.status}`);
        
        const queryString = queryParts.join("&");
        return `/api/v1/en/employee/teacher/live-class/list?${queryString}`;
      },
      transformResponse: (response) => {
        return response?.data?.data || { classes: [], countDocuments: 0 };
      },
      providesTags: ["TeacherLiveClass"],
    }),

    createLiveClass: builder.mutation({
      query: (data) => ({
        url: '/api/v1/en/employee/teacher/live-class/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TeacherLiveClass'],
    }),

    updateLiveClass: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/employee/teacher/live-class/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLiveClass', id }],
    }),

    deleteLiveClass: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/employee/teacher/live-class/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeacherLiveClass'],
    }),

    // Lecture endpoints (aliases for teacher lecture endpoints)
    getLectures: builder.query({
      query: (params = {}) => {
        const queryString = new URLSearchParams();
        if (params.courseId) queryString.append('courseId', params.courseId);
        if (params.courseModuleId) queryString.append('courseModuleId', params.courseModuleId);
        if (params.limit) queryString.append('limit', params.limit);
        if (params.offset) queryString.append('offset', params.offset);
        
        return `/api/v1/en/employee/teacher/lectures/list?${queryString.toString()}`;
      },
      transformResponse: (response) => {
        return response?.data?.data || { lectures: [], total: 0 };
      },
      providesTags: ['TeacherLecture'],
    }),

    getLecture: builder.query({
      query: (id) => `/api/v1/en/employee/teacher/lectures/view/${id}`,
      transformResponse: (response) => {
        return response?.data?.data || {};
      },
      providesTags: (result, error, id) => [{ type: 'TeacherLecture', id }],
    }),

    createLecture: builder.mutation({
      query: (data) => ({
        url: '/api/v1/en/employee/teacher/lectures/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['TeacherLecture'],
    }),

    uploadLecture: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/employee/teacher/lectures/upload/${id}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLecture', id }],
    }),

    updateLecture: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/employee/teacher/lectures/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLecture', id }],
    }),

    deleteLecture: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/employee/teacher/lectures/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TeacherLecture'],
    }),

    uploadLectureHandout: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/employee/teacher/lectures/upload/handout/${id}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLecture', id }],
    }),

    uploadLectureNotes: builder.mutation({
      query: ({ id, formData }) => ({
        url: `/api/v1/en/employee/teacher/lectures/upload/notes/${id}`,
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLecture', id }],
    }),

    deleteLectureHandout: builder.mutation({
      query: ({ lectureId, handoutId }) => ({
        url: `/api/v1/en/employee/teacher/lectures/delete-handout/${lectureId}/${handoutId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { lectureId }) => [{ type: 'TeacherLecture', id: lectureId }],
    }),

    deleteLectureNotes: builder.mutation({
      query: ({ lectureId, notesId }) => ({
        url: `/api/v1/en/employee/teacher/lectures/delete-notes/${lectureId}/${notesId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { lectureId }) => [{ type: 'TeacherLecture', id: lectureId }],
    }),

    // Timezone endpoint
    listTimezones: builder.query({
      query: ({ limit = 500 } = {}) => 
        `/api/v1/en/timezone/list-all?limit=${limit}`,
      transformResponse: (response) => {
        const timezones = response?.data?.data?.timeZone?.timeZone || [];
        return Array.isArray(timezones) ? timezones : [];
      },
      providesTags: ["Timezones"],
    }),

    // Course Materials endpoints (teacher version)
    getCourseMaterials: builder.query({
      query: (params = {}) => {
        const queryString = new URLSearchParams();
        if (params.courseId) queryString.append('courseId', params.courseId);
        if (params.courseModuleId) queryString.append('courseModuleId', params.courseModuleId);
        if (params.limit) queryString.append('limit', params.limit);
        if (params.offset) queryString.append('offset', params.offset);
        
        return `/api/v1/en/employee/teacher/course-material/list?${queryString.toString()}`;
      },
      transformResponse: (response) => {
        return response?.data?.data || { materials: [], total: 0 };
      },
      providesTags: ['CourseMaterial'],
    }),

    deleteCourseMaterial: builder.mutation({
      query: (id) => ({
        url: `/api/v1/en/employee/teacher/course-material/delete/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CourseMaterial'],
    }),

    // Course list endpoint for teacher
    getCourseList: builder.query({
      query: () => '/api/v1/en/employee/teacher/course/list-all',
      transformResponse: (response) => {
        return response?.data?.data?.courses || [];
      },
      providesTags: ['TeacherCourse'],
    }),

    getAllCourses: builder.query({
      query: () => '/api/v1/en/employee/teacher/course/list-all',
      transformResponse: (response) => {
        return response?.data?.data?.courses || [];
      },
      providesTags: ['TeacherCourse'],
    }),

    // Course staff (teachers) endpoint
    getAllCourseStaff: builder.query({
      query: ({ limit = 1000 } = {}) => 
        `/api/v1/en/admin/employee/teachers/get-all?isTeacher=3`,
      transformResponse: (response) => {
        return response?.data?.data || { data: [], totalCount: 0 };
      },
      providesTags: ["CourseStaff"],
    }),

    // Course Material uploads
    uploadCourseMaterial: builder.mutation({
      query: ({ formData }) => ({
        url: '/api/v1/en/employee/teacher/course-material/upload',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['CourseMaterial'],
    }),

    updateCourseMaterial: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/en/employee/teacher/course-material/update/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'CourseMaterial', id }],
    }),

    getCourseMaterial: builder.query({
      query: (id) => `/api/v1/en/employee/teacher/course-material/view/${id}`,
      transformResponse: (response) => {
        return response?.data?.data || {};
      },
      providesTags: (result, error, id) => [{ type: 'CourseMaterial', id }],
    }),

    // Status update mutations (for teacher - updates their own content)
    // Note: Teachers can update status of their own classes
    updateLiveClassStatus: builder.mutation({
      query: ({ id, status, rejectReason }) => ({
        url: `/api/v1/en/employee/teacher/live-class/update-status/${id}`,
        method: 'PATCH',
        body: { status, rejectReason },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLiveClass', id }],
    }),

    updateLectureStatus: builder.mutation({
      query: ({ id, status, rejectReason }) => ({
        url: `/api/v1/en/employee/teacher/lectures/update-status/${id}`,
        method: 'PATCH',
        body: { status, rejectReason },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLecture', id }],
    }),

    updateClassRangeById: builder.mutation({
      query: ({ id, status, rejectReason }) => ({
        url: `/api/v1/en/employee/teacher/live-class/update-status/${id}`,
        method: 'PATCH',
        body: { status, rejectReason },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'TeacherLiveClass', id }],
    }),
  }),
});

export const {
  useCreateTeacherLiveClassMutation,
  useGetTeacherLiveClassQuery,
  useGetTeacherLiveClassesQuery,
  useUpdateTeacherLiveClassMutation,
  useDeleteTeacherLiveClassMutation,
  useCreateTeacherLectureMutation,
  useGetTeacherLectureQuery,
  useUploadTeacherLectureMutation,
  useUpdateTeacherLectureVideoMutation,
  useUpdateTeacherLectureMutation,
  useGetTeacherLecturesQuery,
  useDeleteTeacherLectureMutation,
  useGetTeacherCoursesForClassesQuery,
  useGetTeacherCourseQuery,
  useListTeacherTimezonesQuery,
  useGetAllTeachersQuery,
  useGetClassRangeQuery,
  useGetClassRangesQuery,
  useDeleteClassRangeMutation,
  useUpdateTeacherClassRangeMutation,
  useGetLiveClassQuery,
  useUpdateIndividualClassMutation,
  // Alias exports for compatibility
  useGetLiveClassesQuery,
  useCreateLiveClassMutation,
  useUpdateLiveClassMutation,
  useDeleteLiveClassMutation,
  useGetLecturesQuery,
  useGetLectureQuery,
  useCreateLectureMutation,
  useUploadLectureMutation,
  useUpdateLectureMutation,
  useDeleteLectureMutation,
  useUploadLectureHandoutMutation,
  useUploadLectureNotesMutation,
  useDeleteLectureHandoutMutation,
  useDeleteLectureNotesMutation,
  useListTimezonesQuery,
  useGetCourseMaterialsQuery,
  useDeleteCourseMaterialMutation,
  useGetCourseListQuery,
  useGetAllCoursesQuery,
  useGetAllCourseStaffQuery,
  useUploadCourseMaterialMutation,
  useUpdateCourseMaterialMutation,
  useGetCourseMaterialQuery,
  useUpdateLiveClassStatusMutation,
  useUpdateLectureStatusMutation,
  useUpdateClassRangeByIdMutation,
} = teacherClassesApiSlice;

// Export alias for compatibility
export const useUpdateClassRangeMutation = useUpdateTeacherClassRangeMutation;

export const teacherClassesSlice = createSlice({
  name: 'teacherClasses',
  initialState: {
    liveClasses: [],
    recordedLectures: [],
    loading: false,
    error: null,
    activeTab: 'live', // 'live' or 'recorded'
    activeModuleTab: 'listening',
    activeViewTab: 'cards',
    filters: {
      status: 'all',
      level: 'all',
      instructor: 'all',
      category: 'all'
    },
    pagination: {
      currentPage: 1,
      totalPages: 1,
      itemsPerPage: 12
    }
  },
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
    },
    
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    
    setActiveModuleTab: (state, action) => {
      state.activeModuleTab = action.payload;
    },
    
    setActiveViewTab: (state, action) => {
      state.activeViewTab = action.payload;
    },
    
    setLiveClasses: (state, action) => {
      state.liveClasses = action.payload;
    },
    
    setRecordedLectures: (state, action) => {
      state.recordedLectures = action.payload;
    },
    
    addLiveClass: (state, action) => {
      state.liveClasses.push(action.payload);
    },
    
    updateLiveClass: (state, action) => {
      const index = state.liveClasses.findIndex(cls => cls.id === action.payload.id);
      if (index !== -1) {
        state.liveClasses[index] = action.payload;
      }
    },
    
    removeLiveClass: (state, action) => {
      state.liveClasses = state.liveClasses.filter(cls => cls.id !== action.payload);
    },
    
    addRecordedLecture: (state, action) => {
      state.recordedLectures.push(action.payload);
    },
    
    updateRecordedLecture: (state, action) => {
      const index = state.recordedLectures.findIndex(lec => lec.id === action.payload.id);
      if (index !== -1) {
        state.recordedLectures[index] = action.payload;
      }
    },
    
    removeRecordedLecture: (state, action) => {
      state.recordedLectures = state.recordedLectures.filter(lec => lec.id !== action.payload);
    },
    
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    resetFilters: (state) => {
      state.filters = {
        status: 'all',
        level: 'all',
        instructor: 'all',
        category: 'all'
      };
    }
  }
});

export const {
  setLoading,
  setError,
  setActiveTab,
  setActiveModuleTab,
  setActiveViewTab,
  setLiveClasses,
  setRecordedLectures,
  addLiveClass,
  updateLiveClass,
  removeLiveClass,
  addRecordedLecture,
  updateRecordedLecture,
  removeRecordedLecture,
  setFilters,
  setPagination,
  resetFilters
} = teacherClassesSlice.actions;

export default teacherClassesSlice.reducer;
